import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_VALUATIONS } from '../../../lib/graphql/queries/contacts';
import type { GQLValuationItem } from './useContact';

const PAGE_SIZE = 50;

interface GetContactValuationsData {
  contact: {
    contactId: string;
    valuations: {
      items: GQLValuationItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactValuationsGQL = (contactId: string) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactValuationsData>(GET_CONTACT_VALUATIONS, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      sortOrder: 'DESC',
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.valuations?.items ?? [];
  const total = data?.contact?.valuations?.total ?? 0;
  const hasMore = data?.contact?.valuations?.hasMore ?? false;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);

  return { items, total, hasMore, loading, error, hasNext: hasMore, handleNextPage };
};
